import Card from '../../components/card/Card';
import H from '../../components/texts/h/H';
import Input from '../../components/input/Input';
import { useClientTheme } from '../../context/ThemeContext';
import { useConfig } from '../../context/ConfigContext';

import { amount, applicationData, clientData } from '../../types/interfaces';
import { CURRENCY_CODES } from '../../utils/Constants';
import { formatCurrency } from '../../utils/Masks';
import { sumApplicationTotalsByCurrency } from '../../utils/applicationTotals';
import { resolveLocalCurrencyCode } from '../../utils/quotationAssets';
import SummaryApplication from '../Origination/Summary/Application';
import styled from 'styled-components';
import { useLocation } from 'react-router-dom';
import { useMemo } from 'react';

const ResponsiveContainer = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 20px;
  align-items: stretch;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`;

const TableContainer = styled.div`
  min-width: 0;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  overflow-x: auto;
`;

const SummaryContainer = styled.div`
  min-width: 0;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
`;

const SummaryCardWrapper = styled.div`
  width: 100%;
  max-width: 100%;
  min-width: 0;
  height: 100%;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  box-sizing: border-box;
  word-wrap: break-word;
  overflow-wrap: anywhere;
  white-space: normal;
  gap: 8px;
`;

const ColumnContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 8px;
  flex: 1 1 0;
  min-width: 0;
  max-width: 100%;
`;

const InputRowContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  width: 100%;
  
  & > h3 {
    min-width: 120px;
    white-space: nowrap;
  }
  
  & input {
    background-color: transparent !important;
  }
`;

interface ISummaryAmountsProps {
  clientData: clientData;
  isManualAmount: boolean;
  amounts: Array<amount>;
  products: Array<applicationData>;
  hideFianzaPagare?: boolean;
  editable?: boolean;
  onNoteAmountChange?: (value: number) => void;
  onGuaranteeAmountChange?: (value: number) => void;
  /** Moneda nacional (ARS por defecto; UYU en Cementos Uruguay). */
  localCurrencyCode?: string;
}
const SummaryAmounts = ({
  clientData,
  isManualAmount,
  amounts,
  products,
  hideFianzaPagare = false,
  editable = false,
  onNoteAmountChange,
  onGuaranteeAmountChange,
  localCurrencyCode: localCurrencyCodeProp,
}: ISummaryAmountsProps) => {
  const location = useLocation();
  const { clientTheme } = useClientTheme();
  const config = useConfig();
  const localCurrencyCode =
    localCurrencyCodeProp ?? resolveLocalCurrencyCode(config);
  const isInstrumentationPath =
    location?.pathname?.includes('/instrumentacion') ?? false;

  const totals = useMemo(() => {
    const { local, usd } = sumApplicationTotalsByCurrency(
      products,
      localCurrencyCode,
      CURRENCY_CODES.usd
    );
    return { totalLocal: local, totalUsd: usd };
  }, [products, localCurrencyCode]);

  const renderCurrencyAmount = (
    currencyCode: string,
    requestedAmount: Array<amount>,
    totalAmount: number | string
  ) => {
    const currencyData = requestedAmount.find(
      (item) => item.currency_code === currencyCode
    );
    const amount = currencyData ? currencyData.value : totalAmount;
    return formatCurrency(amount.toString());
  };

  return (
    <ResponsiveContainer data-testid="summary-amounts-layout">
      <TableContainer data-testid="summary-amounts-table">
        <SummaryApplication applications={products}></SummaryApplication>
      </TableContainer>
      <SummaryContainer data-testid="summary-amounts-totals">
        <Card 
          border={true} 
          variant={clientTheme ? 'primary' : 'success'} 
          modifier='weak' 
          spacing={'1'}
          width='100%'
          maxWidth='100%'
          height='100%'
        >
          <SummaryCardWrapper>
            <ColumnContainer>
              <H
                type='2'
                withMargin={false}
                weight='bold'
                variant={clientTheme ? 'primary' : 'success'}
                align='center'
              >
                {localCurrencyCode}{' '}
                {isManualAmount
                  ? renderCurrencyAmount(
                    localCurrencyCode,
                    amounts,
                    totals.totalLocal
                  )
                  : formatCurrency(totals.totalLocal)}
              </H>
              <H
                type='2'
                withMargin={false}
                weight='bold'
                variant={clientTheme ? 'primary' : 'success'}
                align='center'
              >
                {CURRENCY_CODES.usd}{' '}
                {isManualAmount
                  ? renderCurrencyAmount(
                    CURRENCY_CODES.usd,
                    amounts,
                    totals.totalUsd
                  )
                  : formatCurrency(totals.totalUsd)}
              </H>
            </ColumnContainer>

            {isInstrumentationPath && !hideFianzaPagare && (
              <ColumnContainer>
                {editable ? (
                  <>
                    <InputRowContainer>
                      <H type='3' variant={clientTheme ? 'primary' : 'success'} weight='bold' withMargin={false}>
                        Valor fianza
                      </H>
                      <Input
                        type='text'
                        value={formatCurrency(clientData.note_amount || '0').toString()}
                        onChange={(e) => {
                          const numericValue = e.target.value.replace(/[^0-9]/g, '');
                          onNoteAmountChange?.(Number(numericValue));
                        }}
                      />
                    </InputRowContainer>
                    <InputRowContainer>
                      <H type='3' variant={clientTheme ? 'primary' : 'success'} weight='bold' withMargin={false}>
                        Valor pagaré
                      </H>
                      <Input
                        type='text'
                        value={formatCurrency(clientData.guarantee_amount || '0').toString()}
                        onChange={(e) => {
                          const numericValue = e.target.value.replace(/[^0-9]/g, '');
                          onGuaranteeAmountChange?.(Number(numericValue));
                        }}
                      />
                    </InputRowContainer>
                  </>
                ) : (
                  <>
                    <H
                      type='2'
                      withMargin={false}
                      weight='bold'
                      variant={clientTheme ? 'primary' : 'success'}
                      align='center'
                    >
                      Valor fianza {formatCurrency(clientData.note_amount || '0')}
                    </H>
                    <H
                      type='2'
                      withMargin={false}
                      weight='bold'
                      variant={clientTheme ? 'primary' : 'success'}
                      align='center'
                    >
                      Valor pagaré {formatCurrency(clientData.guarantee_amount || '0')}
                    </H>
                  </>
                )}
              </ColumnContainer>
            )}
          </SummaryCardWrapper>
        </Card>
      </SummaryContainer>
    </ResponsiveContainer>
  );
};
export default SummaryAmounts;
